/**
 * Parsing of the user manual (`docs/manuale-utente.md`) into the addressable
 * sections the in-app documentation page navigates, anchors and searches.
 *
 * The Markdown file stays the single source of truth: nothing here rewrites it,
 * the parser only slices it on its own headings.
 */

/** One heading of the manual together with the Markdown that belongs to it. */
export interface ManualSection {
  /** Anchor id, matching the slug a Markdown renderer would give the heading. */
  id: string
  /** Heading depth (2 for `##`, 3 for `###`, …), used to nest the index. */
  level: number
  /** Heading text, as shown in the index. */
  title: string
  /** The section's raw Markdown, its own heading included. */
  markdown: string
}

const HEADING = /^(#{1,6})\s+(.+?)\s*$/
/** A list item holding a single link to another section of the same document. */
const TOC_ENTRY = /^(?:[-*+]|\d+\.)\s+\[[^\]]+\]\(#[^)]+\)$/
const CODE_FENCE = /^\s*(```|~~~)/
/** Everything that is neither a letter, a digit, a space nor a dash is dropped. */
const NON_SLUG_CHARS = /[^\p{L}\p{N}\s-]/gu

/**
 * Slugify a heading the way GitHub-style Markdown anchors do, so the manual's
 * own cross-references (`[…](#3-collegare-laccount-hubspot)`) keep working.
 */
function slugify(heading: string): string {
  return heading
    .trim()
    .toLowerCase()
    .replace(NON_SLUG_CHARS, '')
    .replace(/\s+/g, '-')
}

/** Disambiguate repeated headings with a numeric suffix, as Markdown renderers do. */
function uniqueId(slug: string, seen: Map<string, number>): string {
  const occurrences = seen.get(slug) ?? 0
  seen.set(slug, occurrences + 1)
  return occurrences === 0 ? slug : `${slug}-${occurrences}`
}

/** Split the manual's Markdown source into its sections. */
export function parseManual(source: string): ManualSection[] {
  const sections: ManualSection[] = []
  const seenIds = new Map<string, number>()
  let current: { section: ManualSection; lines: string[] } | null = null
  let insideCodeFence = false

  const closeCurrent = () => {
    if (!current) return
    current.section.markdown = current.lines.join('\n').trim()
    sections.push(current.section)
    current = null
  }

  for (const line of source.split('\n')) {
    if (CODE_FENCE.test(line)) insideCodeFence = !insideCodeFence

    const heading = insideCodeFence ? null : HEADING.exec(line)
    if (!heading) {
      if (current) current.lines.push(line)
      continue
    }

    const [, hashes, text] = heading
    closeCurrent()
    // The document title is the page's own heading, not a section of it.
    if (hashes.length === 1) continue

    current = {
      section: {
        id: uniqueId(slugify(text), seenIds),
        level: hashes.length,
        title: text,
        markdown: '',
      },
      lines: [line],
    }
  }
  closeCurrent()

  return sections
}

/**
 * A section that is nothing but a list of links to other sections — the
 * manual's own table of contents, which the guide page replaces with its
 * side index instead of rendering twice.
 */
export function isTableOfContents(section: ManualSection): boolean {
  const body = section.markdown
    .split('\n')
    .slice(1)
    .map((line) => line.trim())
    .filter((line) => line !== '' && line !== '---')

  return body.length > 0 && body.every((line) => TOC_ENTRY.test(line))
}

/** Sections whose heading or body contains `query`, case-insensitively. */
export function filterSections(sections: ManualSection[], query: string): ManualSection[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return sections
  return sections.filter((section) => section.markdown.toLowerCase().includes(needle))
}
